import type {
  ReadableStore,
  Selection,
  SelectionRef,
  ViewportSize,
  ViewportState,
} from '@frontend/viewer-core';

import { buildAccents } from './accentBuilder';
import type {
  DrawingModel,
  DxfRenderTarget,
  MarqueePreview,
  OverlayState,
} from './types';

type Dependencies = {
  viewportStore: ReadableStore<ViewportState>;
  sizeStore: ReadableStore<ViewportSize>;
  modelStore: ReadableStore<DrawingModel | null>;
  /** Subscribed, to know when to re-push the accents and the hidden-layer mask. */
  hiddenLayers: ReadableStore<ReadonlySet<number>>;
  getSelection: () => Selection;
  getHovered: () => SelectionRef | null;
  getHoveredLayer: () => number | null;
  overlays: ReadableStore<OverlayState>;
  /** Fires when anything the accent overlay is drawn from changes, and not otherwise. */
  accentSource: ReadableStore<unknown>;
  preview: ReadableStore<MarqueePreview | null>;
};

/**
 * Wires session state into a renderer and keeps it in sync.
 *
 * Returns a function matching `DxfViewerSession.attachRenderer` exactly. Each
 * push is a cheap setter that no-ops when nothing changed and otherwise asks
 * the driver for a frame, so a burst of pointer events still draws once.
 */
export const createRendererBinding =
  ({
    viewportStore,
    sizeStore,
    modelStore,
    hiddenLayers,
    getSelection,
    getHovered,
    getHoveredLayer,
    overlays,
    accentSource,
    preview,
  }: Dependencies) =>
  (target: DxfRenderTarget): (() => void) => {
    const pushView = () =>
      target.setView(viewportStore.getState(), sizeStore.getState());
    const pushScene = () => {
      const model = modelStore.getState();
      target.setScene(model?.scene ?? null, model?.pmiEntityKeys);
    };
    const pushHidden = () => target.setHiddenLayers(hiddenLayers.getState());
    const pushAccents = () =>
      target.setAccents(
        buildAccents(
          modelStore.getState(),
          {
            selection: getSelection(),
            hovered: getHovered(),
            hoveredLayer: getHoveredLayer(),
            hiddenLayers: hiddenLayers.getState(),
          },
          preview.getState(),
        ),
      );
    const pushDisplay = () => {
      const current = overlays.getState();
      target.setPmiVisible(current.pmi);
      target.setLayerColoring(current.layerColoring);
    };

    const unsubscribes = [
      viewportStore.subscribe(pushView),
      sizeStore.subscribe(pushView),
      modelStore.subscribe(pushScene),
      modelStore.subscribe(pushAccents),
      accentSource.subscribe(pushAccents),
      // The preview only republishes when the catch actually changes, so this
      // does not rebuild accent geometry as the box sweeps within one set.
      preview.subscribe(pushAccents),
      hiddenLayers.subscribe(pushHidden),
      overlays.subscribe(pushDisplay),
    ];

    // The renderer arrives after the drawing has usually already loaded, so seed
    // it with everything rather than waiting for the next change.
    pushScene();
    pushHidden();
    pushAccents();
    pushDisplay();
    pushView();

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
